import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtMxn } from "@/context/CartContext";
import { ExternalLink, X } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["all", "pending_payment", "paid", "failed", "cancelled", "refunded", "partially_refunded"];
const STATUS_STYLES = {
  pending_payment: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  paid: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  failed: "bg-red-500/10 text-red-300 border-red-500/30",
  cancelled: "bg-gray-500/10 text-gray-400 border-gray-700",
  refunded: "bg-brand-orange-500/10 text-brand-orange-300 border-primary/30",
  partially_refunded: "bg-brand-orange-500/10 text-brand-orange-300 border-primary/30",
};

export default function StaffOrdenes() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => {
    const q = new URLSearchParams();
    if (status !== "all") q.set("status", status);
    if (search) q.set("search", search);
    api.get(`/admin/orders?${q}`).then((r) => setItems(r.data.items));
  };
  useEffect(() => { load(); }, [status, search]);

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Órdenes</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Órdenes y pagos</h1>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => setStatus(s)}
                data-testid={`orders-filter-${s}`}
                aria-pressed={status === s}
                className={`text-xs px-2 py-1 rounded uppercase font-semibold tracking-widest ${
                  status === s ? "bg-[#F26A21] text-[#0B0B0B]" : "text-gray-400 bg-white/5 hover:bg-white/10"
                }`}>{s.replace("_", " ")}</button>
            ))}
          </div>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por número, email o nombre..."
            data-testid="orders-search"
            className="flex-1 min-w-[240px] bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-1.5 text-sm text-white" />
        </div>

        <div className="mt-6 overflow-x-auto border border-gray-800 rounded-xl" data-testid="staff-orders-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">Número</th>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">Cliente</th>
                <th className="text-left px-3 py-2">Items</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-gray-500">Sin órdenes.</td></tr>
              )}
              {items.map((o) => (
                <tr key={o.id} className="border-t border-gray-800" data-testid={`staff-order-row-${o.order_number}`}>
                  <td className="px-3 py-2 font-mono text-white">{o.order_number}</td>
                  <td className="px-3 py-2 text-xs text-gray-300">{o.created_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2 text-xs text-gray-300">{o.contact?.email}</td>
                  <td className="px-3 py-2 text-white font-mono">{(o.items || []).length}</td>
                  <td className="px-3 py-2 text-right font-mono text-white">{fmtMxn(o.total_mxn)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold border ${STATUS_STYLES[o.status] || STATUS_STYLES.pending_payment}`}>{o.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setSelected(o)} data-testid={`staff-order-open-${o.order_number}`}
                      className="inline-flex items-center gap-1 text-xs text-[#93C5FD] hover:text-white">
                      Abrir <ExternalLink className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <OrderModal order={selected} onClose={() => { setSelected(null); load(); }} />}
    </StaffLayout>
  );
}

function OrderModal({ order, onClose }) {
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState(order.total_mxn);
  const [refundReason, setRefundReason] = useState("");
  const [processing, setProcessing] = useState(false);

  const canRefund = order.status === "paid" || order.status === "partially_refunded";
  const canCancel = order.status === "pending_payment" || order.status === "failed";

  const doRefund = async () => {
    if (!refundReason.trim() || refundReason.length < 3) { toast.error("Ingresa razón (mín 3 caracteres)"); return; }
    setProcessing(true);
    try {
      await api.post(`/admin/orders/${order.id}/refund`, { amount_mxn: parseInt(refundAmount, 10), reason: refundReason });
      toast.success("Reembolso aplicado");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setProcessing(false); }
  };

  const doCancel = async () => {
    setProcessing(true);
    try {
      await api.post(`/admin/orders/${order.id}/cancel`);
      toast.success("Orden cancelada");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setProcessing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="order-modal"
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#F26A21]">Orden</p>
            <h3 className="text-lg font-mono text-white">{order.order_number}</h3>
            <p className="text-xs text-gray-400 mt-0.5">Estado: {order.status}</p>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="text-gray-400"><X /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-3 text-xs">
          <div className="p-3 bg-[#0B0F14] border border-gray-800 rounded">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Cliente</p>
            <p className="mt-1 text-white">{order.contact?.name}</p>
            <p className="text-gray-400">{order.contact?.email}</p>
            <p className="text-gray-400">{order.contact?.company}</p>
          </div>
          <div className="p-3 bg-[#0B0F14] border border-gray-800 rounded space-y-1">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Totales</p>
            <p className="text-gray-400 flex justify-between">Subtotal <span className="text-white font-mono">{fmtMxn(order.subtotal_mxn)}</span></p>
            <p className="text-gray-400 flex justify-between">Descuento <span className="text-emerald-400 font-mono">-{fmtMxn(order.discount_mxn || 0)}</span></p>
            <p className="text-gray-400 flex justify-between">IVA <span className="text-white font-mono">{fmtMxn(order.tax_mxn)}</span></p>
            <p className="text-white flex justify-between font-bold pt-1 border-t border-gray-800">Total <span className="text-[#F26A21] font-mono">{fmtMxn(order.total_mxn)}</span></p>
          </div>
        </div>
        <div className="space-y-1 text-sm">
          <p className="text-[10px] uppercase tracking-widest text-gray-500">Programas ({(order.items || []).length})</p>
          {(order.items || []).map((it, i) => (
            <div key={i} className="p-2 bg-[#0B0F14] border border-gray-800 rounded text-xs" data-testid={`order-item-${i}`}>
              <p className="text-white">{it.program_snapshot?.title}</p>
              <p className="text-gray-500 font-mono">Cohorte {it.cohort_snapshot?.name} · {fmtMxn(it.unit_price_mxn)}</p>
            </div>
          ))}
        </div>
        {(order.refunds || []).length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Refunds</p>
            <ul className="space-y-1 text-xs mt-1">
              {order.refunds.map((r) => (
                <li key={r.id} className="text-gray-400"><span className="text-emerald-400 font-mono">{fmtMxn(r.amount_mxn)}</span> — {r.reason}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-800">
          {canRefund && (
            <button onClick={() => setRefundOpen(true)} data-testid="refund-open-btn"
              className="text-xs px-3 py-1.5 rounded bg-red-500 hover:bg-red-600 text-white font-semibold">Reembolsar</button>
          )}
          {canCancel && (
            <button onClick={doCancel} disabled={processing} data-testid="cancel-order-btn"
              className="text-xs px-3 py-1.5 rounded border border-gray-700 text-gray-200 hover:bg-white/5 disabled:opacity-40">Cancelar orden</button>
          )}
        </div>

        {refundOpen && (
          <div className="mt-3 p-3 bg-[#0B0F14] border border-red-500/40 rounded space-y-2" data-testid="refund-form">
            <p className="text-xs text-red-300 font-semibold">Reembolso</p>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-gray-500">Monto MXN</span>
              <input type="number" min={1} max={order.total_mxn}
                value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                data-testid="refund-amount"
                className="w-full mt-1 bg-[#111827] border border-gray-800 rounded px-2 py-1 text-sm text-white" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-gray-500">Razón</span>
              <textarea rows={2} value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                data-testid="refund-reason"
                className="w-full mt-1 bg-[#111827] border border-gray-800 rounded px-2 py-1 text-sm text-white" />
            </label>
            <button onClick={doRefund} disabled={processing} data-testid="refund-confirm"
              className="text-xs px-3 py-1.5 rounded bg-red-500 hover:bg-red-600 text-white font-semibold disabled:opacity-40">
              Confirmar reembolso
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
